import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
  getAllWorkspacesData,
  getOnboardingStartUrlWithParams,
  getProductUrl,
  joinRouteMatchPath,
  PREDICT_PRODUCT_SLUG,
  QUERY_PARAMS,
  ROUTES
} from '@futrli/shared-services';

import { SfaLoading } from '../../components/sfa/sfa-loading';
import { useMarkSfaUserSetupComplete } from '../../hooks/use-mark-sfa-user-setup-complete';
import { useSfaOnboardingContext } from '../../contexts/sfa-onboarding-context';

interface SfaFinishedContainerProps {
  workspaceId: string;
  organisationId: string;
  datasourceId: string;
}

export const SfaFinishedContainer = ({ workspaceId, organisationId, datasourceId }: SfaFinishedContainerProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { businessId, currentUser, workspace } = useSfaOnboardingContext();
  const { markSfaUserSetupComplete } = useMarkSfaUserSetupComplete();
  const [userUpdated, setUserUpdated] = useState(false);

  // currentUser object will change, so using a ref here to take a snapshot.
  const userState = useRef({
    email: currentUser?.email || currentUser?.attributes?.email || '',
    onboardingRequired: !currentUser?.setupCompleted
  });

  // Refreshing the Redux workspace cache can rerender this flow before navigation unmounts it.
  // Guard the completion side effects so a rerender cannot start another forced API request.
  const redirectHandled = useRef(false);
  const isReadyToRedirect = !userState.current.onboardingRequired || userUpdated;

  useEffect(() => {
    const updateUser = async () => {
      if (businessId && workspace) {
        try {
          await markSfaUserSetupComplete(workspace.name, businessId, userState.current.email);
        } catch {
          // Updating the user is best effort and must not block the redirect.
        } finally {
          // Always set this to true (even on error) so that we dont block the user flow
          setUserUpdated(true);
        }
      }
    };

    if (userState.current.onboardingRequired) {
      updateUser();
    }
  }, [businessId, markSfaUserSetupComplete, workspace]);

  useEffect(() => {
    if (!isReadyToRedirect || redirectHandled.current) {
      return;
    }

    redirectHandled.current = true;

    if (workspace) {
      // Keep this fire-and-forget: the redirect must not wait for the refreshed workspace data.
      dispatch(getAllWorkspacesData(true));
    }

    if (userState.current.onboardingRequired) {
      navigate(
        getOnboardingStartUrlWithParams(workspaceId, organisationId, datasourceId, {
          businessId: businessId || undefined
        })
      );
      return;
    }

    navigate(
      joinRouteMatchPath(
        '',
        getProductUrl(workspaceId, organisationId, PREDICT_PRODUCT_SLUG),
        ROUTES.HOME,
        businessId ? `?${QUERY_PARAMS.BUSINESS_ID}=${businessId}` : ''
      )
    );
  }, [businessId, datasourceId, dispatch, isReadyToRedirect, navigate, organisationId, workspace, workspaceId]);

  // Keep meaningful dialog content visible until the navigation above unmounts the SFA flow.
  return <SfaLoading />;
};
